import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { safeReturnTo } from './returnTo.js';
import { createSignInRedirectGuard } from './signInRedirectGuard.js';
import { withSingleFlightSilentSignIn } from './singleFlight.js';

const DEFAULTS = Object.freeze({ url: 'http://localhost:8180', realm: 'mitus', clientId: 'mitus-frontend' });
// Refresh tokens rotate and cannot be reused, so every renewal path (automatic renewal,
// session restore, API 401 retries) must share one in-flight refresh request.
const SingleFlightUserManager = withSingleFlightSilentSignIn(UserManager);
let userManager = null;

export function readKeycloakConfig() {
  const runtime = (typeof window !== 'undefined' && window.__MITUS_RUNTIME_CONFIG__) || {};
  return {
    url: String(runtime.keycloakUrl || process.env.REACT_APP_MITUS_KEYCLOAK_URL || DEFAULTS.url).replace(/\/$/, ''),
    realm: String(runtime.keycloakRealm || process.env.REACT_APP_MITUS_KEYCLOAK_REALM || DEFAULTS.realm),
    clientId: String(runtime.keycloakClientId || process.env.REACT_APP_MITUS_KEYCLOAK_CLIENT_ID || DEFAULTS.clientId),
  };
}

function manager() {
  if (userManager) return userManager;
  const config = readKeycloakConfig();
  const origin = window.location.origin;
  userManager = new SingleFlightUserManager({
    authority: `${config.url}/realms/${config.realm}`,
    client_id: config.clientId,
    redirect_uri: `${origin}/`,
    post_logout_redirect_uri: `${origin}/`,
    response_type: 'code',
    scope: 'openid profile email',
    automaticSilentRenew: true,
    monitorSession: false,
    loadUserInfo: false,
    // Tab-scoped so a reload keeps the session; refresh tokens rotate on every use (realm setting).
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  });
  return userManager;
}

function redirectGuard() {
  return createSignInRedirectGuard({ storage: window.sessionStorage });
}

// Counts sessions the API rejected although their token was still valid (for example a
// backend that refuses every Keycloak token). Unlike the redirect history, a completed
// sign-in does not reset it, so such a loop still stops after a few rounds.
function rejectionGuard() {
  return createSignInRedirectGuard({ storage: window.sessionStorage, key: 'mitus.signInRejections' });
}

// Returns false once too many sessions were rejected inside the guard window.
export function recordSessionRejection() {
  return rejectionGuard().tryRecord();
}

export function resetSessionRejections() {
  rejectionGuard().reset();
}

function isSignInCallback() {
  const params = new URLSearchParams(window.location.search);
  return params.has('state') && (params.has('code') || params.has('error'));
}

// Finishes a returning sign-in, or returns the tab's stored user as-is (possibly with an
// expired access token; the caller renews it through the single-flight renewal).
export async function loadOidcUser() {
  const um = manager();
  if (isSignInCallback()) {
    let user;
    try {
      user = await um.signinRedirectCallback();
    } catch (error) {
      // oidc-client-ts consumes the one-time state even on failure; drop the stale callback parameters.
      window.history.replaceState(window.history.state, '', '/');
      throw error;
    }
    // A completed sign-in ends any redirect streak.
    resetSignInRedirectGuard();
    window.history.replaceState(window.history.state, '', safeReturnTo(user.state?.returnTo));
    return user;
  }
  return (await um.getUser()) || null;
}

export function startSignInRedirect({ onFailure } = {}) {
  if (!redirectGuard().tryRecord()) return false;
  const returnTo = isSignInCallback() ? '/' : safeReturnTo(`${window.location.pathname}${window.location.search}${window.location.hash}`);
  const um = manager();
  um.removeUser().catch(() => {})
    .then(() => loadProviderMetadata(um))
    .then(() => um.signinRedirect({ state: { returnTo } }))
    .catch((error) => { if (onFailure) onFailure(error); });
  return true;
}

// Fetch (and cache) Keycloak's metadata before a redirect. When signinRedirect or
// signoutRedirect fails after preparing the navigation, oidc-client-ts calls
// window.stop(), which also aborts the app's pending chunk loads; failing here first avoids that.
function loadProviderMetadata(um) {
  return um.metadataService.getMetadata();
}

export function resetSignInRedirectGuard() {
  redirectGuard().reset();
}

// Single-flight: concurrent callers share one refresh-token request.
export function renewOidcUser() {
  return manager().signinSilent();
}

export async function signOutRedirect() {
  const um = manager();
  const user = await um.getUser().catch(() => null);
  await um.removeUser().catch(() => {});
  await loadProviderMetadata(um);
  await um.signoutRedirect(user?.id_token ? { id_token_hint: user.id_token } : {});
}

// onUnloaded: the stored user was removed. onAccessTokenExpired: the access token's lifetime ran out.
// onRenewError(error): automatic renewal failed (the ErrorResponse / network error is passed on).
export function onOidcUserChange({ onLoaded, onUnloaded, onAccessTokenExpired, onRenewError }) {
  const events = manager().events;
  events.addUserLoaded(onLoaded);
  events.addUserUnloaded(onUnloaded);
  events.addAccessTokenExpired(onAccessTokenExpired);
  events.addSilentRenewError(onRenewError);
  return () => {
    events.removeUserLoaded(onLoaded);
    events.removeUserUnloaded(onUnloaded);
    events.removeAccessTokenExpired(onAccessTokenExpired);
    events.removeSilentRenewError(onRenewError);
  };
}
